import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PublicLayout } from '../components/PublicLayout.js';

const SUPPORT_EMAIL = 'support@arunavm.com';

interface Section {
  id: string;
  title: string;
  body: ReactNode;
}

const SECTIONS: Section[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    body: (
      <>
        <p>
          Velinko has two parts: an Android app that reads incoming SMS on your phone, and this
          website, where you read them. You need both.
        </p>
        <ol>
          <li>
            <strong>Request access.</strong> Velinko is invite-only during the beta.{' '}
            <Link to="/register">Request access</Link> with the email you want to use. You will see
            &quot;request received&quot; until the Velinko team approves the email.
          </li>
          <li>
            <strong>Create your account.</strong> Once approved, register with that email and a
            password of at least 8 characters, or use <em>Sign in with Google</em> with the same
            email.
          </li>
          <li>
            <strong>Install the Android app</strong> and pair it (below).
          </li>
        </ol>
        <div className="callout">
          Your phone needs Android 13 or newer. Velinko is built and tested on Samsung Galaxy phones
          with One UI.
        </div>
      </>
    ),
  },
  {
    id: 'install',
    title: 'Install the Android app',
    body: (
      <>
        <p>
          The app is distributed through Google Play internal testing. After your email is approved
          you receive a Play testing link. Open it on your phone, accept the invite, then install{' '}
          <strong>Velinko</strong> from Google Play.
        </p>
        <p>On first start the app asks for these permissions:</p>
        <ul>
          <li>
            <strong>SMS</strong> (receive, read and send) to forward messages and to send the
            replies you write in the browser.
          </li>
          <li>
            <strong>Notifications</strong> for the small &quot;Velinko is syncing&quot; notice
            Android requires for background work.
          </li>
        </ul>
        <p>Velinko starts again by itself after the phone restarts. You do not need to open it.</p>
      </>
    ),
  },
  {
    id: 'pair',
    title: 'Pair your phone',
    body: (
      <ol>
        <li>
          Sign in on the <Link to="/dashboard">dashboard</Link> and choose <strong>+ Pair</strong>.
          A 6-digit code appears. It is valid for 5 minutes.
        </li>
        <li>Open the Velinko app on the phone and type the code.</li>
        <li>
          Allow the permissions. The phone shows up under <em>Devices</em> as{' '}
          <strong>Online</strong>, and your recent messages start to appear.
        </li>
      </ol>
    ),
  },
  {
    id: 'samsung',
    title: 'Keep sync reliable on Samsung',
    body: (
      <>
        <p>
          One UI stops background apps aggressively. Do this once after installing, or codes may
          arrive late:
        </p>
        <ol>
          <li>
            <strong>Settings → Apps → Velinko → Battery</strong> and choose{' '}
            <strong>Unrestricted</strong>.
          </li>
          <li>
            <strong>Settings → Battery → Background usage limits</strong>: make sure Velinko is not
            in <em>Sleeping apps</em> or <em>Deep sleeping apps</em>. Adding it to{' '}
            <em>Never sleeping apps</em> is best.
          </li>
          <li>
            <strong>Settings → Apps → Velinko → Mobile data</strong>: turn on{' '}
            <em>Allow background data usage</em>.
          </li>
          <li>
            Leave the &quot;Velinko is syncing&quot; notification on. Hiding it can let Android stop
            the app.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: 'dashboard',
    title: 'Using the dashboard',
    body: (
      <ul>
        <li>
          <strong>OTP codes</strong> show as a green chip next to the conversation and in the
          message. Click the chip to copy the code.
        </li>
        <li>
          <strong>Search</strong> filters conversations by number or text. <em>View archive</em>{' '}
          shows threads you archived.
        </li>
        <li>
          <strong>Send</strong> a reply from the composer. The phone sends it as a normal SMS from
          your SIM, and your carrier&apos;s rates apply.
        </li>
        <li>
          <strong>Sync now</strong> asks the phone to upload anything waiting.{' '}
          <strong>Force full resync</strong> uploads the phone&apos;s whole SMS history again; use
          it only if messages are missing.
        </li>
        <li>
          <strong>Diagnostics</strong> shows battery, network, queue and error details from the
          phone.
        </li>
      </ul>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    body: (
      <dl className="faq">
        <dt>&quot;Account not approved&quot; when signing in</dt>
        <dd>
          Your email is not on the invite list yet, or access was removed. Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from that address.
        </dd>
        <dt>The phone shows Offline</dt>
        <dd>
          Open the app once, check the phone has internet, and repeat the{' '}
          <a href="#samsung">Samsung battery settings</a>. Offline phones still receive SMS; they
          upload as soon as they reconnect.
        </dd>
        <dt>A code arrived on the phone but not here</dt>
        <dd>
          Choose <strong>Sync now</strong>. If the queue in Diagnostics keeps growing, the phone
          cannot reach the server: check mobile data and background data.
        </dd>
        <dt>&quot;Update required&quot; on a device</dt>
        <dd>
          Update Velinko from Google Play. Old versions stop syncing once they are no longer
          supported.
        </dd>
        <dt>Pairing code rejected</dt>
        <dd>
          Codes expire after 5 minutes. After 5 wrong attempts pairing is paused for 10 minutes;
          generate a new code and try again.
        </dd>
      </dl>
    ),
  },
  {
    id: 'account',
    title: 'Your account and data',
    body: (
      <>
        <ul>
          <li>
            <strong>Download</strong> everything Velinko stores for you from the{' '}
            <Link to="/account">Account</Link> page.
          </li>
          <li>
            <strong>Unpair a phone</strong> with <em>Delete</em> on its device card. Its synced
            messages are removed from the server; the messages on the phone are untouched.
          </li>
          <li>
            <strong>Delete your account</strong> on the <Link to="/account">Account</Link> page. It
            is immediate and cannot be undone.
          </li>
        </ul>
        <p>
          Read the <Link to="/privacy">privacy policy</Link> for what is stored, where and for how
          long.
        </p>
      </>
    ),
  },
];

export default function DocsPage(): JSX.Element {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <PublicLayout wide>
      <div className="site-inner docs">
        <aside className="docs-toc" aria-label="On this page">
          <span className="section-label">Documentation</span>
          <nav>
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`}>
                {s.title}
              </a>
            ))}
          </nav>
        </aside>
        <article className="docs-body prose">
          <h1>Velinko docs</h1>
          <p className="lead">
            Everything you need to set up Velinko, keep it syncing and fix the usual problems.
          </p>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="docs-section">
              <h2>
                <a href={`#${s.id}`} className="anchor">
                  {s.title}
                </a>
              </h2>
              {s.body}
            </section>
          ))}
          <p className="muted">
            Still stuck? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </article>
      </div>
    </PublicLayout>
  );
}
